import PendingVodCard from "./PendingVodCard";
import { usePendingVodUploads } from "./pendingVodUploads";
import ReviewCardMetadata from "./ReviewCardMetadata";
import ReviewSetupEditor from "./ReviewSetupEditor";
// /src/components/Review/VideoList.js
// /src/components/Review/VideoList.js
import { collection, getDocs, onSnapshot, query, orderBy } from "firebase/firestore";

import { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "../../Security/firebase-config";
import { useTeam } from "../../context/TeamContext";
import FocusPoints from "./FocusPoints";
import ReviewedButton from "./ReviewedButton";
import LinkedDeletionDialog from '../core-components/LinkedDeletionDialog';
import CustomAlert from "../core-components/CustomAlert";
import LinkVodDialog from "../core-components/LinkVodDialog";
import { triggerTeamQuotaRefresh } from "../../utils/teamQuota";

import "./VideoList.css";

const hasPlayableReviewMedia = (video) =>
  !!video?.storagePath ||
  !!video?.videoURL ||
  !!video?.url ||
  (video?.playbackMode === "segment_playlist" && video?.status !== "recording");

const shouldShowReviewVideo = (video) =>
  hasPlayableReviewMedia(video) ||
  video?.status === "processing" ||
  video?.status === "failed";

export default function VideoList({
  mapId,
  onSelectVideo,
  refreshTrigger,
  sortMethod,
  selectedTags,
  filterMode,
  hideReviewedByMe,
  setAvailableTags,
}) {
  const [videos, setVideos] = useState([]);
  const [allVideos,setAllVideos]=useState([]);
  const [deleteDialogState, setDeleteDialogState] = useState(null);
  const { teamId, username } = useTeam();
  const pendingUploads = usePendingVodUploads(teamId, mapId, allVideos);

// IMPORTANT:
  // expandedKeys holds either:
  // - MATCH::<matchId>  (for match cards)
  // - VIDEO::<videoId>  (for legacy cards)
  const [expandedKeys, setExpandedKeys] = useState([]);

  // focusPointsMap caches by the same stable key:
  // - MATCH::<matchId>
  // - VIDEO::<videoId>
  const [focusPointsMap, setFocusPointsMap] = useState({});

  // Relevant Review State (match-based; legacy stored under LEGACY::<videoId>)
  const [unreviewedCountsByMatch, setUnreviewedCountsByMatch] = useState({});

  // ── EDIT MODE STATE ──
  // We use a stable key for editing so it doesn't depend on which POV is "primary"
  // - For match cards:  editingVideoId = MATCH::<matchId>
  // - For legacy cards: editingVideoId = <videoId>
  const [editingVideoId, setEditingVideoId] = useState(null);

// Linking to Stats
  const [linkingVideo, setLinkingVideo] = useState(null);

  // Custom Alerts
  const [customAlert, setCustomAlert] = useState(null);

  // Match titles cache (review matches)
  const [matchTitleById, setMatchTitleById] = useState({});
  const [matchPlayedAtById, setMatchPlayedAtById] = useState({});
  useEffect(() => {
    setMatchTitleById({}); setMatchPlayedAtById({});
    if (!teamId || !mapId) return undefined;
    return onSnapshot(collection(db, "teams", teamId, "maps", mapId, "matches"), snapshot => {
      const titles = {}, dates = {};
      snapshot.docs.forEach(item => {const data = item.data(); titles[item.id] = data.title || ""; dates[item.id] = data.matchPlayedAt || data.createdAt || null;});
      setMatchTitleById(titles); setMatchPlayedAtById(dates);
    }, error => console.warn("Could not subscribe to match setup", error));
  }, [teamId, mapId]);

  const [linkRefreshKey, setLinkRefreshKey] = useState(0);

// ── LOADING STATUS ──
  
   // 'success' | 'error' | null
  const [initialLoading, setInitialLoading] = useState(true);

// Helper: compute unreviewed count for one match (notes + replies)
  const computeUnreviewedForMatch = useCallback(async (matchId) => {
    if (!teamId || !mapId || !matchId) return 0;

    let unreviewed = 0;

    // 1) Notes: matches/{matchId}/notes
    const notesRef = collection(
      db,
      "teams",
      teamId,
      "maps",
      mapId,
      "matches",
      matchId,
      "notes"
    );
    const notesSnap = await getDocs(notesRef);

    for (const noteDoc of notesSnap.docs) {
      const note = noteDoc.data() || {};
      const mentions = Array.isArray(note.at) ? note.at : [];

      const relevantToUser =
        mentions.includes("@Team") || mentions.includes(`@${username}`);

      const userHasNotViewed =
        !Array.isArray(note.viewers) || !note.viewers.includes(username);

      if (relevantToUser && userHasNotViewed) unreviewed += 1;
    }

    // 2) Replies: matches/{matchId}/allReplies
    const repliesRef = collection(
      db,
      "teams",
      teamId,
      "maps",
      mapId,
      "matches",
      matchId,
      "allReplies"
    );
    const repliesSnap = await getDocs(repliesRef);

    for (const rDoc of repliesSnap.docs) {
      const reply = rDoc.data() || {};
      const viewers = Array.isArray(reply.viewers) ? reply.viewers : [];
      const notViewedByMe = !viewers.includes(username);
      const notMyReply = reply.createdBy !== username;

      if (notViewedByMe && notMyReply) unreviewed += 1;
    }

    return unreviewed;
  }, [mapId, teamId, username]);

  // Fallback for legacy videos that still have notes under videos/{videoId}/notes
  const computeUnreviewedForLegacyVideo = useCallback(async (videoId) => {
    if (!teamId || !mapId || !videoId) return 0;

    let unreviewed = 0;

    const notesRef = collection(
      db,
      "teams",
      teamId,
      "maps",
      mapId,
      "videos",
      videoId,
      "notes"
    );
    const notesSnap = await getDocs(notesRef);

    for (const noteDoc of notesSnap.docs) {
      const note = noteDoc.data() || {};
      const mentions = Array.isArray(note.at) ? note.at : [];

      const relevantToUser =
        mentions.includes("@Team") || mentions.includes(`@${username}`);

      const userHasNotViewed =
        !Array.isArray(note.viewers) || !note.viewers.includes(username);

      if (relevantToUser && userHasNotViewed) unreviewed += 1;
    }

    const repliesRef = collection(
      db,
      "teams",
      teamId,
      "maps",
      mapId,
      "videos",
      videoId,
      "allReplies"
    );
    const repliesSnap = await getDocs(repliesRef);

    for (const rDoc of repliesSnap.docs) {
      const reply = rDoc.data() || {};
      const viewers = Array.isArray(reply.viewers) ? reply.viewers : [];
      const notViewedByMe = !viewers.includes(username);
      const notMyReply = reply.createdBy !== username;

      if (notViewedByMe && notMyReply) unreviewed += 1;
    }

    return unreviewed;
  }, [mapId, teamId, username]);

useEffect(() => {
    let active=true, generation=0, hasServerSnapshot=false;
    setVideos([]);setAllVideos([]);setUnreviewedCountsByMatch({});setInitialLoading(true);setExpandedKeys([]);setFocusPointsMap({});setEditingVideoId(null);setDeleteDialogState(null);
    if(!teamId||!mapId){setInitialLoading(false);return undefined;}
    const stop=onSnapshot(collection(db,'teams',teamId,'maps',mapId,'videos'),{includeMetadataChanges:true},snapshot=>{
      if(!active||(hasServerSnapshot&&snapshot.metadata?.fromCache))return;
      if(!snapshot.metadata?.fromCache)hasServerSnapshot=true;
      const version=++generation;
      const raw=snapshot.docs.map(item=>({...item.data(),id:item.id,mapId})).filter(video=>!video.reviewMovedTo&&shouldShowReviewVideo(video));
      setAllVideos(raw);
      setAvailableTags([...new Set(raw.flatMap(video=>video.tags||[]))].sort());
      const filters=(selectedTags||[]).filter(tag=>tag!=='__NOT_REVIEWED__');
      const visible=raw.filter(video=>(!filters.length||(filterMode==='AND'?filters.every(tag=>(video.tags||[]).includes(tag)):filters.some(tag=>(video.tags||[]).includes(tag))))&&(!(hideReviewedByMe||(selectedTags||[]).includes('__NOT_REVIEWED__'))||!(video.reviewedBy||[]).includes(username)));
      visible.sort((a,b)=>sortMethod==='title'?String(a.title||'').localeCompare(String(b.title||'')):(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      setVideos(visible);setInitialLoading(false);
      const keys=[...new Set(visible.map(video=>video.matchId||'LEGACY::'+video.id))];
      Promise.all(keys.map(async key=>[key,await(key.startsWith('LEGACY::')?computeUnreviewedForLegacyVideo(key.slice(8)):computeUnreviewedForMatch(key))])).then(counts=>{if(active&&version===generation)setUnreviewedCountsByMatch(Object.fromEntries(counts));}).catch(error=>console.warn('Could not load relevant review counts',error));
    },error=>{if(active){generation++;setVideos([]);setAllVideos([]);setInitialLoading(false);setCustomAlert({message:'Could not load the VOD list. Check your access and retry.',type:'error',position:'top'});}console.warn('Could not subscribe to VODs',error);});
    return()=>{active=false;generation++;stop();};
  },[teamId,mapId,refreshTrigger,linkRefreshKey,selectedTags,filterMode,hideReviewedByMe,username,sortMethod,setAvailableTags,computeUnreviewedForMatch,computeUnreviewedForLegacyVideo]);

  // Group: matchId => array of videos
  // No POV selector in the UI; we pick a deterministic "primaryVideo" (newest createdAt).
  const groupedCards = useMemo(() => {
    const matchGroups = new Map(); // matchId -> videos[]
    const legacy = []; // videos without matchId

    for (const v of videos) {
      if (v.matchId) {
        if (!matchGroups.has(v.matchId)) matchGroups.set(v.matchId, []);
        matchGroups.get(v.matchId).push(v);
      } else {
        legacy.push(v);
      }
    }

    const matchCards = Array.from(matchGroups.entries()).map(([matchId, vids]) => {
      const primary =
        vids
          .slice()
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0] || null;

      const matchTitle = (matchTitleById?.[matchId] || "").trim();
      const displayTitle = matchTitle || primary?.title || `Match ${matchId.slice(0, 8)}`;

      const matchPlayedAt = matchPlayedAtById?.[matchId] || primary?.createdAt || null;
      const createdBy = primary?.createdBy || "Unknown";
      const comp = primary?.comp || [];

      const tagsUnion = Array.from(new Set((vids || []).flatMap((x) => x.tags || [])));
      const processingCount = vids.filter((x) => x.status === "processing").length;
      const failedCount = vids.filter((x) => x.status === "failed").length;

      return {
        type: "match",
        id: `MATCH::${matchId}`,
        matchId,
        videos: vids,
        primaryVideo: primary,
        displayTitle,
        matchPlayedAt,
        createdBy,
        comp,
        tagsUnion,
        povCount: vids.length,
        processingCount,
        failedCount,
      };
    });

    // sort cards
    const sortedMatchCards = matchCards.sort((a, b) => {
      if (sortMethod === "title") {
        return (a.displayTitle || "").localeCompare(b.displayTitle || "");
      }
      return (b.matchPlayedAt?.seconds || 0) - (a.matchPlayedAt?.seconds || 0);
    });

    const legacyCards = legacy.map((v) => ({
      type: "legacy",
      id: v.id,
      video: v,
    }));

    return [...sortedMatchCards, ...legacyCards];
  }, [videos, sortMethod, matchTitleById, matchPlayedAtById]);

  

  

  const openDeleteDialog = (candidateVideos, title, initialSelectedIds = []) => {
    const uniqueVideos = Array.from(
      new Map((candidateVideos || []).filter(Boolean).map((video) => [video.id, video])).values()
    );

    if (!uniqueVideos.length) return;

    const seedIds = initialSelectedIds.filter((id) => uniqueVideos.some((video) => video.id === id));
    const selectedIds =
      seedIds.length > 0
        ? seedIds
        : uniqueVideos.length === 1
          ? [uniqueVideos[0].id]
          : [];

    setDeleteDialogState({
      title: title || uniqueVideos[0]?.title || "this VOD",
      videos: uniqueVideos,
      selectedIds,
    });
  };

// ─────────────────────────────────────────────
  // Focus Points: load from MATCH if matchId exists, else legacy VIDEO
  // Cache key:
  //  - MATCH::<matchId>
  //  - VIDEO::<videoId>
  // Expand key uses the same stable key
  // ─────────────────────────────────────────────
  async function loadFocusPoints({ videoId, matchId }) {
    let coll;

    if (matchId) {
      coll = collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "focusPoints");
    } else {
      coll = collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "focusPoints");
    }

    const qRef = query(coll, orderBy("order", "asc"));
    const snap = await getDocs(qRef);
    const pts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const cacheKey = matchId ? `MATCH::${matchId}` : `VIDEO::${videoId}`;
    setFocusPointsMap((prev) => ({ ...prev, [cacheKey]: pts }));
  }

  const toggleFocusPoints = async ({ videoId, matchId }) => {
    const key = matchId ? `MATCH::${matchId}` : `VIDEO::${videoId}`;
    const isExpanded = expandedKeys.includes(key);

    if (isExpanded) {
      setExpandedKeys((prev) => prev.filter((k) => k !== key));
      return;
    }

    if (!focusPointsMap[key]) {
      await loadFocusPoints({ videoId, matchId });
    }

    setExpandedKeys((prev) => [...prev, key]);
  };

  const handleDeleteConfirmed = async (result) => {
   const deletedPaths = new Set(result.deletedVideoPaths || []);
   setVideos(current => current.filter(video => !deletedPaths.has(`teams/${teamId}/maps/${mapId}/videos/${video.id}`)));
   setFocusPointsMap({}); setExpandedKeys([]); setDeleteDialogState(null);
   triggerTeamQuotaRefresh({teamId});
   setCustomAlert({message:'Selected linked items deleted.',type:'success',position:'top'});
 };

  // notify logic (kept as-is)
  const handleNotifyVodReady = async (video) => {
    try {
      const token = await auth.currentUser.getIdToken(true);
      const url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/notifyVodReady`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teamId, mapId, videoId: video.id }),
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      setCustomAlert({
        message:
          `VOD ready notification sent to the team. ` +
          `(in-app: ${data.inApp ?? 0}, Discord: ${data.discord ?? 0})`,
        type: "success",
        position: "top",
      });
    } catch (e) {
      console.error("notify VOD ready failed:", e);
      setCustomAlert({
        message: "Could not send VOD ready notification.",
        type: "error",
        position: "top",
      });
    }
  };

  return initialLoading ? (
    <div className="video-list video-list--loading">
      <div className="video-skeleton" />
      <div className="video-skeleton" />
      <div className="video-skeleton" />
    </div>
  ) : (
    <>
      <div className="video-list">
        {pendingUploads.map(upload => <PendingVodCard key={upload.id} {...upload} />)}
        {groupedCards.map((card) => {
          // ─────────────────────────────────────────────
          // MATCH CARD (one list item per match)
          // ─────────────────────────────────────────────
          if (card.type === "match") {
            const matchId = card.matchId;
            const primaryVideo = card.primaryVideo;
            const canNotifyReady =
              !!primaryVideo &&
              (primaryVideo.status === "ready" ||
                (!primaryVideo.status && hasPlayableReviewMedia(primaryVideo)));

            // Editing is match-scoped (stable key)
            const editKey = `MATCH::${matchId}`;
            const isEditing = editingVideoId === editKey;

            const unreviewedCount = unreviewedCountsByMatch[matchId] ?? 0;

            const focusKey = `MATCH::${matchId}`;
            const isExpanded = expandedKeys.includes(focusKey);

            // default clickable video (primary POV)
            const clickableVideoId = primaryVideo?.id || null;

            // selected POV in edit mode (defaults to primary POV when opening edit)

return (
              <div
                key={card.id}
                className="video-item"
                onClick={() =>
                  isEditing ? null : clickableVideoId ? onSelectVideo(clickableVideoId) : null
                }
              >
                {isEditing ? (
                  /* ── EDIT MODE (edits match title + selected POV's povName/tags/comp/sync) ── */
                  <ReviewSetupEditor teamId={teamId} mapId={mapId} matchId={matchId} mode="match" video={primaryVideo} videos={card.videos} onAttachExisting={() => setLinkingVideo({mapId, reviewMatchId:matchId})} onCancel={()=>setEditingVideoId(null)} onSaved={()=>{setEditingVideoId(null);setCustomAlert({message:"VOD Setup updated.",type:"success",position:"top"});}}/>
                ) : (
                  /* ── READ-ONLY MODE ── */
                  <>
                    <div className="video-info">
                      <div className="video-text-content">
                        <h4 className="video-title">{card.displayTitle}</h4>

                        <><p className="video-meta">Match ID: {matchId.slice(0,8)}</p><ReviewCardMetadata factsTargetId={`web-vod-facts-${teamId}-${mapId}-${matchId}`} teamId={teamId} mapId={mapId} matchId={matchId} records={allVideos.filter(record=>record.matchId===matchId)}/></>

                        {(card.processingCount > 0 || card.failedCount > 0) && (
                          <div className="video-meta-row" style={{ marginTop: "0.35rem" }}>
                            <p className="video-meta" style={{ opacity: 0.9 }}>
                              {card.processingCount > 0
                                ? `Processing ${card.processingCount} POV${card.processingCount === 1 ? "" : "s"}`
                                : `Failed ${card.failedCount} POV${card.failedCount === 1 ? "" : "s"}`}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="icon-group-wrapper" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="notify-btn"
                          title="Notify all teammates that this primary POV VOD is ready"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canNotifyReady) handleNotifyVodReady(primaryVideo);
                          }}
                          aria-label="Notify all teammates that this VOD is ready"
                          disabled={!canNotifyReady}
                        >
                          📣
                        </button>

                        <div
                          className={`review-status-badge ${unreviewedCount === 0 ? "all-reviewed" : "needs-review"}`}
                          title={
                            unreviewedCount === 0
                              ? "All relevant notes/replies reviewed"
                              : `${unreviewedCount} unreviewed relevant notes/replies`
                          }
                        >
                          {unreviewedCount}
                        </div>

                        {primaryVideo ? (
                          <ReviewedButton teamId={teamId} mapId={mapId} videoId={primaryVideo.id} />
                        ) : null}

                      </div>
                    </div>

                    <div className="video-actions ReviewCardActionRow">
                      {primaryVideo ? (
                        <button
                          className="video-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFocusPoints({ videoId: primaryVideo.id, matchId });
                          }}
                          title={isExpanded ? "Hide focus points" : "Show focus points"}
                        >
                          Focus points
                        </button>
                      ) : null}

                      <div className="ReviewCardActionFacts" id={`web-vod-facts-${teamId}-${mapId}-${matchId}`} />
                    <div className="video-actions-right">

<button
                          className="video-btn edit-btn video-btn--icon-only"
                          onClick={(e) => {
                            e.stopPropagation();

                            setEditingVideoId(editKey);
                          }}
                          title="Edit event Setup and POV settings"
                          aria-label="Edit event Setup and POV settings"
                        >
                          <span className="video-btn__sr">Edit match / POV</span>
                        </button>

                        {primaryVideo ? (
                          <button
                            className="video-btn VideoList-delete-btn video-btn--icon-only"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteDialog(card.videos, card.displayTitle, [primaryVideo.id]);
                            }}
                            title={
                              card.videos.length > 1
                                ? "Delete one or more POVs from this cloud match"
                                : "Delete this cloud POV for the whole team"
                            }
                            aria-label="Delete POV"
                          >
                            <span className="video-btn__sr">Delete POV</span>
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: "1rem" }} onClick={(e) => e.stopPropagation()}>
                        <FocusPoints
                          libraryOnlyWhenEditing
                          focusPoints={focusPointsMap[focusKey] || []}
                          videoId={primaryVideo?.id || null}
                          matchId={matchId}
                          mapId={mapId}
                          teamId={teamId}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          }

          // ─────────────────────────────────────────────
          // LEGACY CARD (no matchId) — kept so old data doesn't break anything
          // ─────────────────────────────────────────────
          const video = card.video;
          const isEditing = editingVideoId === video.id;

          const matchId = video.matchId || null;

          const unreviewedCount = matchId
            ? unreviewedCountsByMatch[matchId] ?? 0
            : unreviewedCountsByMatch[`LEGACY::${video.id}`] ?? 0;

          const focusKey = `VIDEO::${video.id}`;
          const isExpanded = expandedKeys.includes(focusKey);

          return (
            <div key={video.id} className="video-item" onClick={() => (isEditing ? null : onSelectVideo(video.id))}>
              {isEditing ? (
                /* ── EDIT MODE ── */
                <ReviewSetupEditor teamId={teamId} mapId={mapId} matchId={video.matchId} video={video} onCancel={()=>setEditingVideoId(null)} onSaved={()=>{setEditingVideoId(null);setCustomAlert({message:"POV updated.",type:"success",position:"top"});}}/>
              ) : (
                /* ── READ-ONLY MODE ── */
                <>
                  <div className="video-info">
                    <div className="video-text-content">
                      <h4 className="video-title">{video.title}</h4>

                      <ReviewCardMetadata factsTargetId={`web-vod-facts-${teamId}-${mapId}-${video.id}`} teamId={teamId} mapId={mapId} matchId={matchId} records={[video]}/>

                      {!matchId && (
                        <div className="video-meta-row" style={{ marginTop: "0.25rem" }}>
                          <p className="video-meta" style={{ opacity: 0.9 }}>
                            Legacy upload (no match yet)
                          </p>
                        </div>
                      )}

                      {video.status === "processing" && (
                        <p className="video-meta" style={{ opacity: 0.9 }}>
                          Processing upload before playback becomes available.
                        </p>
                      )}

                      {video.status === "failed" && (
                        <p className="video-meta" style={{ opacity: 0.9 }}>
                          Video processing failed for this upload.
                        </p>
                      )}

                    </div>

                    <div className="icon-group-wrapper" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="notify-btn"
                        title="Notify all teammates that this VOD is ready"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (video.status === "ready" || (!video.status && hasPlayableReviewMedia(video))) {
                            handleNotifyVodReady(video);
                          }
                        }}
                        aria-label="Notify all teammates that this VOD is ready"
                        disabled={video.status === "processing" || video.status === "failed"}
                      >
                        📣
                      </button>

                      <div
                        className={`review-status-badge ${unreviewedCount === 0 ? "all-reviewed" : "needs-review"}`}
                        title={
                          unreviewedCount === 0
                            ? "All relevant notes/replies reviewed"
                            : `${unreviewedCount} unreviewed relevant notes/replies`
                        }
                      >
                        {unreviewedCount}
                      </div>

                      <ReviewedButton
                        teamId={teamId}
                        mapId={mapId}
                        videoId={video.id}
                        matchId={video.matchId || null}
                      />

                    </div>
                  </div>

                  <div className="video-actions">
                    <button
                      className="video-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFocusPoints({ videoId: video.id, matchId: null });
                      }}
                      title={isExpanded ? "Hide focus points" : "Show focus points"}
                    >
                      Focus points
                    </button>

                    <div className="ReviewCardActionFacts" id={`web-vod-facts-${teamId}-${mapId}-${video.id}`} />
                    <div className="video-actions-right">

<button
                        className="video-btn edit-btn video-btn--icon-only"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingVideoId(video.id);

}}
                        title="Edit POV title, sync and event Setup"
                        aria-label="Edit POV title, sync and event Setup"
                      >
                        <span className="video-btn__sr">Edit match / POV</span>
                      </button>

                      <button
                        className="video-btn VideoList-delete-btn video-btn--icon-only"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog([video], video.title, [video.id]);
                        }}
                        title="Delete this cloud POV for the whole team"
                        aria-label="Delete POV"
                      >
                        <span className="video-btn__sr">Delete POV</span>
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: "1rem" }} onClick={(e) => e.stopPropagation()}>
                      <FocusPoints
                          libraryOnlyWhenEditing
                        focusPoints={focusPointsMap[focusKey] || []}
                        videoId={video.id}
                        matchId={null}
                        mapId={mapId}
                        teamId={teamId}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {deleteDialogState && (
          <LinkedDeletionDialog teamId={teamId} anchor={{kind:'vod',mapId,videoId:deleteDialogState.videos[0].id}} seedVideoIds={deleteDialogState.selectedIds.length ? deleteDialogState.selectedIds : deleteDialogState.videos.map(video => video.id)} onCancel={() => setDeleteDialogState(null)} onDeleted={handleDeleteConfirmed} />
        )}

        {customAlert && (
          <CustomAlert
            message={customAlert.message}
            type={customAlert.type}
            position={customAlert.position}
            onClose={() => setCustomAlert(null)}
          />
        )}

        {linkingVideo && (
          <LinkVodDialog
            teamId={teamId}
            mode="reviewmatch-to-teamstats"
            initialReviewMatch={linkingVideo}
            onClose={() => {
              setLinkingVideo(null);
              setLinkRefreshKey((prev) => prev + 1);
            }}
          />
        )}

</div>
    </>
  );
}

